import { AppIcon } from '../ui/icon'
import { mdiCheckboxBlankOutline, mdiCheckboxMarkedOutline } from '@mdi/js'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { usePropertyDefinition, usePropertyEnumValues, type PropertyEnumValue, type PropertyDefinition } from '../../hooks/useQueries'

// Unicode glyphs reused in <option> labels where JSX icons can't be rendered.
// JSX surfaces render the Lucide icon version below.
export const NEUTRAL_BOOLEAN_TRUE_GLYPH = '☑'
export const NEUTRAL_BOOLEAN_FALSE_GLYPH = '☐'

// Boolean semantic value utility
export const getBooleanDisplay = (value: boolean, semantic?: string, t?: (key: string) => string): ReactNode => {
    const trueLabel = t ? t('values.boolean_true') : 'True'
    const falseLabel = t ? t('values.boolean_false') : 'False'
    switch (semantic) {
        case 'positive':
            return value ? '✅' : '❌'
        case 'negative':
            return value ? '❌' : '✅'
        case 'personal':
            return value ? '👍' : '👎'
        case 'warning':
            return value ? '⚠️' : '✅'
        case 'neutral':
        default: {
            const label = value ? trueLabel : falseLabel
            const icon = value ? mdiCheckboxMarkedOutline : mdiCheckboxBlankOutline
            const tone = value ? 'text-foreground' : 'text-muted-foreground'
            return (
                <span
                    data-testid="boolean-value-neutral"
                    data-boolean-value={value ? 'true' : 'false'}
                    title={label}
                    aria-label={label}
                    role="img"
                    className="inline-flex items-center"
                >
                    <AppIcon path={icon} size="md" className={tone} aria-hidden="true" />
                </span>
            )
        }
    }
}

export const getBooleanOptions = (semantic?: string, t?: (key: string) => string) => {
    const trueLabel = t ? t('values.boolean_true') : 'True'
    const falseLabel = t ? t('values.boolean_false') : 'False'
    switch (semantic) {
        case 'positive':
            return [{ value: 'true', label: `${trueLabel} (✅)` }, { value: 'false', label: `${falseLabel} (❌)` }]
        case 'negative':
            return [{ value: 'true', label: `${trueLabel} (❌)` }, { value: 'false', label: `${falseLabel} (✅)` }]
        case 'personal':
            return [{ value: 'true', label: `${trueLabel} (👍)` }, { value: 'false', label: `${falseLabel} (👎)` }]
        case 'warning':
            return [{ value: 'true', label: `${trueLabel} (⚠️)` }, { value: 'false', label: `${falseLabel} (✅)` }]
        case 'neutral':
        default:
            return [
                { value: 'true', label: `${NEUTRAL_BOOLEAN_TRUE_GLYPH} ${trueLabel}` },
                { value: 'false', label: `${NEUTRAL_BOOLEAN_FALSE_GLYPH} ${falseLabel}` },
            ]
    }
}

// Helper to get enum value display name
export function getEnumValueDisplayName(enumValue: PropertyEnumValue, locale?: string) {
    if (typeof enumValue.name === 'object' && enumValue.name !== null) {
        const names = enumValue.name as Record<string, string>
        if (locale && names[locale]) return names[locale]
        return names.en || names.fr || Object.values(names)[0] || enumValue.value
    }
    return enumValue.value
}

// Resolve display name from a PropertyDefinition
export function getPropertyDefinitionName(propDef: PropertyDefinition | undefined, fallback?: string, locale?: string): string {
    if (!propDef) return fallback || ''

    if (typeof propDef.name === 'object' && propDef.name !== null) {
        const names = propDef.name as Record<string, string>
        if (locale && names[locale]) return names[locale]
        return names.en || names.fr || Object.values(names)[0] || propDef.code || fallback || ''
    }

    return (propDef.name as string) || propDef.code || fallback || ''
}

// Translator for the `common` namespace, as returned by useTranslation('common').
type ConstraintTranslator = (key: string, options?: Record<string, unknown>) => string

// Gather constraint strings for a property definition, localized through `t`
// (the `common` namespace: `property_constraints.*`).
export function getPropertyConstraints(propDef: PropertyDefinition | undefined, t: ConstraintTranslator): string[] {
    if (!propDef) return []
    const constraints: string[] = []

    if (propDef.data_type === 'text' && typeof propDef.properties?.regex_pattern === 'string') {
        constraints.push(t('property_constraints.pattern', { pattern: propDef.properties.regex_pattern, interpolation: { escapeValue: false } }))
    }

    if (propDef.data_type === 'number' && propDef.properties) {
        if (typeof propDef.properties.min_value === 'number') {
            constraints.push(t('property_constraints.min', { value: propDef.properties.min_value }))
        }
        if (typeof propDef.properties.max_value === 'number') {
            constraints.push(t('property_constraints.max', { value: propDef.properties.max_value }))
        }
        if (propDef.properties.integer_only) {
            constraints.push(t('property_constraints.integer_only'))
        }
    }

    if (propDef.data_type === 'enum' && propDef.properties?.multiple === true) {
        constraints.push(t('property_constraints.multiple'))
    }

    if ((propDef.data_type === 'boolean' || propDef.data_type === 'enum') && propDef.flags?.includes('attribute')) {
        constraints.push(t('property_constraints.attribute'))
    }

    return constraints
}

// Enum property display component
/**
 * The display text of an enum value (or several), as plain text: for a place
 * that needs a string — an i18n interpolation, a title — rather than the
 * coloured chip `EnumPropertyDisplay` renders. Falls back to the raw value
 * while the enum values load, or when the property is not an enum.
 */
export function useEnumValueText(org: string, propertyDefinitionUid: string | undefined, value: unknown): string {
    const { i18n } = useTranslation('assets')
    const { data: enumValues = [] } = usePropertyEnumValues(org, propertyDefinitionUid || '')
    return enumValuesText(enumValues, value, i18n.language)
}

/** The pure part of `useEnumValueText`: codes to labels, unknown codes kept as they are. */
export function enumValuesText(enumValues: PropertyEnumValue[], value: unknown, locale?: string): string {
    const values = Array.isArray(value) ? value : [value]
    return values
        .filter((v) => v !== null && v !== undefined && v !== '')
        .map((v) => {
            const hit = enumValues.find((ev) => ev.value === String(v))
            return hit ? getEnumValueDisplayName(hit, locale) : String(v)
        })
        .join(', ')
}

export function EnumPropertyDisplay({ propertyDefinitionUid, value, org }: { propertyDefinitionUid: string, value: string | string[], org: string }) {
    const { i18n } = useTranslation('assets')
    const { data: enumValues = [], isLoading } = usePropertyEnumValues(org, propertyDefinitionUid)
    const { data: propertyDefinition } = usePropertyDefinition(org, propertyDefinitionUid)

    if (isLoading) {
        return <span>{Array.isArray(value) ? value.join(', ') : value}</span>
    }

    // Check if this property supports multiple selection
    const isMultiple = propertyDefinition?.properties?.multiple === true

    // Handle multiple values
    if (isMultiple && Array.isArray(value)) {
        const displayNames = value.map((singleValue) => {
            const enumValue = enumValues.find(ev => ev.value === singleValue)
            if (!enumValue) {
                return singleValue
            }
            const name = getEnumValueDisplayName(enumValue, i18n.language)
            const emoji = enumValue.properties?.emoji as string
            return emoji ? `${emoji} ${name}` : name
        }).sort()

        return <span>{displayNames.join(', ')}</span>
    }

    // Handle single value (original behavior)
    const singleValue = Array.isArray(value) ? value[0] : value
    const enumValue = enumValues.find(ev => ev.value === singleValue)

    if (!enumValue) {
        return <span>{singleValue}</span>
    }

    const displayName = getEnumValueDisplayName(enumValue, i18n.language)
    const emoji = enumValue.properties?.emoji as string
    const label = emoji ? `${emoji} ${displayName}` : displayName

    // Check if this enum value has color properties
    const color = enumValue.properties?.color as string
    if (color) {
        return (
            <div className="inline-flex items-center gap-2">
                <div
                    className="inline-block w-4 h-4 rounded border border-gray-300"
                    style={{ backgroundColor: color }}
                    title={`Color: ${color}`}
                />
                <span>{label}</span>
            </div>
        )
    }

    return <span>{label}</span>
}

// Enum property input component for add form
export function EnumPropertyInput({ propertyDefinitionUid, value, onChange, org, id, testId }: { propertyDefinitionUid: string, value: string, onChange: (value: string) => void, org: string, id?: string, testId?: string }) {
    const { t, i18n } = useTranslation('assets')
    const { data: enumValues = [], isLoading: enumValuesLoading } = usePropertyEnumValues(org, propertyDefinitionUid)
    const { data: propertyDefinition, isLoading: propertyDefLoading } = usePropertyDefinition(org, propertyDefinitionUid)

    if (enumValuesLoading || propertyDefLoading) {
        return (
            <input
                id={id}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder={t('detail.loading_enum_values')}
                disabled
                data-testid={testId}
            />
        )
    }

    const isMultiple = propertyDefinition?.properties?.multiple === true

    if (isMultiple) {
        const selectedValues = value ? value.split(',').map(v => v.trim()).filter(v => v) : []

        return (
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                {enumValues.map((enumValue: PropertyEnumValue) => {
                    const name = getEnumValueDisplayName(enumValue, i18n.language)
                    const emoji = enumValue.properties?.emoji as string
                    const label = emoji ? `${emoji} ${name}` : name
                    return (
                    <label key={enumValue.value} className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={selectedValues.includes(enumValue.value)}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    const newValues = [...selectedValues, enumValue.value]
                                    onChange(newValues.join(', '))
                                } else {
                                    const newValues = selectedValues.filter(v => v !== enumValue.value)
                                    onChange(newValues.join(', '))
                                }
                            }}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <span className="text-sm">
                            {label}
                        </span>
                    </label>
                    )
                })}
            </div>
        )
    } else {
        // Single selection dropdown
        return (
            <select
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid={testId}
            >
                <option value="">{t('detail.select_value_placeholder')}</option>
                {enumValues.map((enumValue: PropertyEnumValue) => {
                    const name = getEnumValueDisplayName(enumValue, i18n.language)
                    const emoji = enumValue.properties?.emoji as string
                    const label = emoji ? `${emoji} ${name}` : name
                    return (
                    <option key={enumValue.value} value={enumValue.value}>
                        {label}
                    </option>
                    )
                })}
            </select>
        )
    }
}

// Enum property edit input component
export function EnumPropertyEditInput({ propertyDefinitionUid, value, onChange, disabled, hasError, org }: { propertyDefinitionUid: string, value: string, onChange: (value: string) => void, disabled: boolean, hasError: boolean, org: string }) {
    const { t, i18n } = useTranslation('assets')
    const { data: enumValues = [], isLoading: enumValuesLoading } = usePropertyEnumValues(org, propertyDefinitionUid)
    const { data: propertyDefinition, isLoading: propertyDefLoading } = usePropertyDefinition(org, propertyDefinitionUid)

    if (enumValuesLoading || propertyDefLoading) {
        return (
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`flex-1 px-2 py-1 border ${hasError ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
                placeholder={t('detail.loading_enum_values')}
                disabled={true}
                autoFocus
            />
        )
    }

    const isMultiple = propertyDefinition?.properties?.multiple === true

    if (isMultiple) {
        const selectedValues = value ? value.split(',').map(v => v.trim()).filter(v => v) : []

        return (
            <div className={`flex-1 space-y-1 ${hasError ? 'border-destructive' : ''}`}>
                <div className="max-h-32 overflow-y-auto border rounded p-2">
                    {enumValues.map((enumValue: PropertyEnumValue) => {
                        const name = getEnumValueDisplayName(enumValue, i18n.language)
                        const emoji = enumValue.properties?.emoji as string
                        const label = emoji ? `${emoji} ${name}` : name
                        return (
                        <label key={enumValue.value} className="flex items-center space-x-2 p-1">
                            <input
                                type="checkbox"
                                checked={selectedValues.includes(enumValue.value)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        const newValues = [...selectedValues, enumValue.value]
                                        onChange(newValues.join(', '))
                                    } else {
                                        const newValues = selectedValues.filter(v => v !== enumValue.value)
                                        onChange(newValues.join(', '))
                                    }
                                }}
                                disabled={disabled}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <span className="text-xs">
                                {label}
                            </span>
                        </label>
                        )
                    })}
                </div>
            </div>
        )
    } else {
        // Single selection dropdown
        return (
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className={`flex-1 px-2 py-1 border ${hasError ? 'border-destructive' : 'border-border'} rounded text-sm bg-background text-foreground`}
                autoFocus
            >
                <option value="">{t('detail.select_value_placeholder')}</option>
                {enumValues.map((enumValue: PropertyEnumValue) => {
                    const name = getEnumValueDisplayName(enumValue, i18n.language)
                    const emoji = enumValue.properties?.emoji as string
                    const label = emoji ? `${emoji} ${name}` : name
                    return (
                    <option key={enumValue.value} value={enumValue.value}>
                        {label}
                    </option>
                    )
                })}
            </select>
        )
    }
}
